<script setup lang="ts">
import { inject, provide } from "vue";

import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import { keyboardNavigation } from "@/directives/keyboardNavigation";

import { menuListConfigKey, menuListItemsContextKey } from "./injectKeys";

const props = defineProps<{
  static?: boolean; // static makes the whole list readonly/non-focusable
  multiselect?: boolean;
  label?: string; // accessibility label
  alwaysRendered?: boolean; // Set true for lists that are always rendered in the view (eg. plan readout objectives)
  selectOnNavigate?: boolean; // Arrow keys click the item they move to
  noScrollOnHover?: boolean; // Prevents scrolling when hovering over list items (default: false)
  role?: string; // ARIA role for the list container (e.g., "menu", "listbox")
}>();

const vKeyboardNavLocal = keyboardNavigation();

// Provide role context for child MenuListItems to determine their own role
provide(menuListItemsContextKey, { role: props.role });

const config = inject(menuListConfigKey, undefined);
const dropdown = inject(dropdownKey, undefined);
</script>

<template>
  <ul v-if="static" tabindex="-1" class="menu-list-items" :role="role || 'listbox'">
    <slot />
  </ul>

  <ul
    v-else
    v-keyboard-nav-local="{
      selector: '.menu-list-item',
      select: selectOnNavigate,
      'soft-focus-initial': alwaysRendered || dropdown?.autofocus?.value === false,
      'ignore-trigger': !alwaysRendered,
      'no-scroll-on-hover': noScrollOnHover,
      'roving-tabindex': true,
    }"
    class="menu-list-items"
    :role="role || 'listbox'"
    :aria-label="label || $t('list.menuList')"
    :aria-multiselectable="config?.multiselect ? 'true' : undefined"
  >
    <slot />
  </ul>
</template>

<style lang="scss" scoped>
.menu-list-items {
  position: relative;
  flex-grow: 1;
  padding: 2px 0;
  display: flex;
  flex-direction: column;
  overflow: hidden auto;
  max-height: 100%;
}
</style>
